// Aba "Preços acordados": o que este fornecedor cobra por cada SKU e desde
// quando. As linhas NÃO são salvas sozinhas — viajam dentro do payload do
// fornecedor, no campo `precosAcordados`, e só existem depois que o modal
// inteiro for salvo.
import React, { useState } from "react";
import * as Casca from "../../../../styles/components/configuration/modals/_casca";
import * as Estilo from "../../../../styles/components/configuration/modals/SupplierModal";
import {
  PrecoAcordado,
  acharSobreposicao,
  descreverVigencia,
  linhaDePrecoVazia,
} from "./tipos";

export interface OpcaoDeSku {
  uuid: string;
  codigo: string;
  nome: string;
}

interface Props {
  precos: PrecoAcordado[];
  skus: OpcaoDeSku[];
  onSalvarLinha: (linha: PrecoAcordado) => void;
  onRemover: (linhaId: string) => void;
}

export const PrecosAcordados: React.FC<Props> = ({
  precos,
  skus,
  onSalvarLinha,
  onRemover,
}) => {
  const [linha, setLinha] = useState<PrecoAcordado>(linhaDePrecoVazia());
  // `editando` guarda o linhaId que está sendo alterado. Sem ele, editar uma
  // linha e confirmar criaria uma segunda linha igual em vez de substituir.
  const [editando, setEditando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  const nomeDoSku = (uuid: string): string => {
    const sku = skus.filter((opcao) => opcao.uuid === uuid)[0];
    if (!sku) return uuid || "(sem SKU)";
    return sku.nome ? `${sku.codigo} — ${sku.nome}` : sku.codigo;
  };

  const alterar = (campo: keyof PrecoAcordado, valor: string) => {
    setLinha((atual) => ({ ...atual, [campo]: valor }));
  };

  const limpar = () => {
    setLinha(linhaDePrecoVazia());
    setEditando(null);
  };

  const confirmarLinha = () => {
    if (!linha.skuUuid) {
      setAviso({ tom: "erro", texto: "Escolha o SKU desta linha de preço." });
      return;
    }
    if (!linha.vigenciaInicio) {
      setAviso({
        tom: "erro",
        texto:
          "Informe o início da vigência: sem data de início não há como saber " +
          "a partir de quando este preço vale.",
      });
      return;
    }
    if (linha.vigenciaFim && linha.vigenciaFim < linha.vigenciaInicio) {
      setAviso({
        tom: "erro",
        texto: "O fim da vigência é anterior ao início.",
      });
      return;
    }

    // A checagem roda sobre a lista COMO ELA FICARIA, com a linha em edição já
    // substituída. Conferir contra a lista atual deixaria a linha colidindo com
    // a própria versão antiga dela mesma.
    const comoFicaria = precos
      .filter((preco) => preco.linhaId !== (editando ?? linha.linhaId))
      .concat(linha);

    const choque = acharSobreposicao(comoFicaria);
    if (choque) {
      setAviso({
        tom: "erro",
        texto:
          `Vigências sobrepostas em ${nomeDoSku(choque.skuUuid)}: ` +
          `${descreverVigencia(choque.primeira)} e ` +
          `${descreverVigencia(choque.segunda)} cobrem os mesmos dias. ` +
          "Com dois preços válidos no mesmo dia, o pedido sairia com o que " +
          "aparecesse primeiro na lista.",
      });
      return;
    }

    onSalvarLinha(linha);
    setAviso({
      tom: "ok",
      texto:
        editando !== null
          ? "Linha alterada. Ela só é gravada quando você salvar o fornecedor."
          : "Linha adicionada. Ela só é gravada quando você salvar o fornecedor.",
    });
    limpar();
  };

  const editarLinha = (preco: PrecoAcordado) => {
    setLinha({ ...preco });
    setEditando(preco.linhaId);
    setAviso(null);
  };

  return (
    <Casca.FormSection>
      <Casca.SectionTitle>Preços acordados</Casca.SectionTitle>

      {precos.length === 0 ? (
        <Estilo.ListaVazia>
          Nenhum preço acordado ainda. Use o formulário abaixo para acrescentar
          o primeiro.
        </Estilo.ListaVazia>
      ) : (
        <Casca.MiniTable>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Unidade de compra</th>
              <th>Preço (R$)</th>
              <th>Vigência</th>
              <th>Qtd. mínima</th>
              <th>Embalagem</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {precos.map((preco) => (
              <tr key={preco.linhaId}>
                <td>{nomeDoSku(preco.skuUuid)}</td>
                <td>{preco.unidadeCompra || "—"}</td>
                {/* A coluna é só de R$. O defeito que existe hoje em produção é
                    "3" e "10%" na mesma coluna: quem lê não sabe qual é qual. */}
                <td>{preco.preco || "—"}</td>
                <td>{descreverVigencia(preco)}</td>
                <td>{preco.quantidadeMinima || "—"}</td>
                <td>{preco.embalagem || "—"}</td>
                <td>
                  <Casca.MiniTableAction
                    type="button"
                    onClick={() => editarLinha(preco)}
                    title="Editar esta linha"
                  >
                    <Casca.MaterialIcons>edit</Casca.MaterialIcons>
                  </Casca.MiniTableAction>
                  <Casca.MiniTableAction
                    type="button"
                    onClick={() => onRemover(preco.linhaId)}
                    title="Remover esta linha"
                  >
                    <Casca.MaterialIcons>delete</Casca.MaterialIcons>
                  </Casca.MiniTableAction>
                </td>
              </tr>
            ))}
          </tbody>
        </Casca.MiniTable>
      )}

      <Estilo.FormularioDeLinha>
        <Casca.LabelInputContainer>
          <Casca.Label>SKU *</Casca.Label>
          <Casca.Select
            value={linha.skuUuid}
            onChange={(e) => alterar("skuUuid", e.target.value)}
          >
            <option value="">Selecione o SKU…</option>
            {skus.map((sku) => (
              <option key={sku.uuid} value={sku.uuid}>
                {sku.codigo}
                {sku.nome ? ` — ${sku.nome}` : ""}
              </option>
            ))}
          </Casca.Select>
        </Casca.LabelInputContainer>

        <Casca.LabelInputContainer>
          <Casca.Label>Unidade de compra</Casca.Label>
          <Casca.Input
            type="text"
            value={linha.unidadeCompra}
            onChange={(e) => alterar("unidadeCompra", e.target.value)}
            placeholder="Ex.: caixa, fardo, kg"
          />
        </Casca.LabelInputContainer>

        <Casca.LabelInputContainer>
          <Casca.Label>Preço (R$)</Casca.Label>
          {/* Dinheiro é Decimal(14,2) no banco: dois decimais, e só R$. */}
          <Casca.Input
            type="number"
            min="0"
            step="0.01"
            value={linha.preco}
            onChange={(e) => alterar("preco", e.target.value)}
            placeholder="0,00"
          />
        </Casca.LabelInputContainer>

        <Casca.LabelInputContainer>
          <Casca.Label>Vigência — início *</Casca.Label>
          <Casca.Input
            type="date"
            value={linha.vigenciaInicio}
            onChange={(e) => alterar("vigenciaInicio", e.target.value)}
          />
        </Casca.LabelInputContainer>

        <Casca.LabelInputContainer>
          <Casca.Label>Vigência — fim</Casca.Label>
          <Casca.Input
            type="date"
            value={linha.vigenciaFim}
            onChange={(e) => alterar("vigenciaFim", e.target.value)}
          />
          <Estilo.ExplicacaoCurta>
            Em branco = vale até você trocar.
          </Estilo.ExplicacaoCurta>
        </Casca.LabelInputContainer>

        <Casca.LabelInputContainer>
          <Casca.Label>Quantidade mínima</Casca.Label>
          {/* Quantidade é Decimal(18,6): seis decimais. */}
          <Casca.Input
            type="number"
            min="0"
            step="0.000001"
            value={linha.quantidadeMinima}
            onChange={(e) => alterar("quantidadeMinima", e.target.value)}
            placeholder="0,000000"
          />
        </Casca.LabelInputContainer>

        <Casca.LabelInputContainer>
          <Casca.Label>Embalagem</Casca.Label>
          <Casca.Input
            type="number"
            min="0"
            step="0.000001"
            value={linha.embalagem}
            onChange={(e) => alterar("embalagem", e.target.value)}
            placeholder="0,000000"
          />
          <Estilo.ExplicacaoCurta>
            Quantos itens vêm na embalagem.
          </Estilo.ExplicacaoCurta>
        </Casca.LabelInputContainer>

        <Estilo.AcoesDaLinha>
          {editando !== null && (
            <Casca.SecondaryButton type="button" onClick={limpar}>
              <Casca.MaterialIcons>close</Casca.MaterialIcons>
              Cancelar edição
            </Casca.SecondaryButton>
          )}
          <Casca.PrimaryButton type="button" onClick={confirmarLinha}>
            <Casca.MaterialIcons>
              {editando !== null ? "check" : "add"}
            </Casca.MaterialIcons>
            {editando !== null ? "Aplicar na linha" : "Acrescentar preço"}
          </Casca.PrimaryButton>
        </Estilo.AcoesDaLinha>
      </Estilo.FormularioDeLinha>

      {aviso && (
        <Casca.AlertMessage $tom={aviso.tom}>{aviso.texto}</Casca.AlertMessage>
      )}

      {skus.length === 0 && (
        <Estilo.NotaDeCampo>
          Nenhum SKU cadastrado nesta organização — a lista acima fica vazia até
          existir SKU na aba <strong>SKU</strong>, ao lado.
        </Estilo.NotaDeCampo>
      )}
    </Casca.FormSection>
  );
};
